import React, { useState, useMemo } from "react";
import { ActionPrompt, ActionPromptRelation } from "./actionPromptTypes";

interface WanActionPromptSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  allPrompts: ActionPrompt[];
  initialTemplatePrompts?: ActionPromptRelation[];
  disabled?: boolean;
  className?: string;
  isBatchMode?: boolean;
}

interface PromptOption {
  id: string;
  name: string;
  text: string;
  enabled: boolean;
  sortOrder: number;
  tagsSummary?: string;
  isHistoricRelation?: boolean;
}

export function WanActionPromptSelect({
  value,
  onChange,
  allPrompts,
  initialTemplatePrompts = [],
  disabled = false,
  className = "",
  isBatchMode = false,
}: WanActionPromptSelectProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Merge prompts: all library prompts + any historic template relations that may not be in allPrompts or are disabled
  const optionsMap = useMemo(() => {
    const map = new Map<string, PromptOption>();

    // 1. Add all library prompts
    for (const p of allPrompts) {
      const tagsSummary = (p.tags || []).map((t) => t.name).join(" · ");
      map.set(p.id, {
        id: p.id,
        name: p.name,
        text: p.text,
        enabled: p.enabled,
        sortOrder: p.sortOrder ?? 0,
        tagsSummary,
      });
    }

    // 2. Overlay or add initial template prompts (preserving historic state)
    for (const rel of initialTemplatePrompts) {
      if (map.has(rel.id)) {
        const existing = map.get(rel.id)!;
        existing.isHistoricRelation = true;
        // Keep prompt's current enabled flag from library, but mark as historic relation
      } else {
        map.set(rel.id, {
          id: rel.id,
          name: rel.name,
          text: rel.text,
          enabled: rel.enabled,
          sortOrder: rel.sortOrder ?? 0,
          isHistoricRelation: true,
        });
      }
    }

    return map;
  }, [allPrompts, initialTemplatePrompts]);

  const allOptions = useMemo(() => {
    const list = Array.from(optionsMap.values());
    list.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name, "zh-CN");
    });
    return list;
  }, [optionsMap]);

  // Filter options by search query
  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter(
      (opt) =>
        opt.name.toLowerCase().includes(q) ||
        opt.text.toLowerCase().includes(q) ||
        (opt.tagsSummary && opt.tagsSummary.toLowerCase().includes(q))
    );
  }, [allOptions, searchQuery]);

  // Selected items list
  const selectedOptions = useMemo(() => {
    return value.map((id) => optionsMap.get(id) || {
      id,
      name: `未知动作 (${id.slice(0, 8)})`,
      text: "",
      enabled: false,
      sortOrder: 0,
      isHistoricRelation: true,
    });
  }, [value, optionsMap]);

  const hasUnavailableSelected = useMemo(() => {
    return selectedOptions.some((opt) => !opt.enabled);
  }, [selectedOptions]);

  const toggleSelect = (id: string) => {
    if (disabled) return;
    if (value.includes(id)) {
      onChange(value.filter((item) => item !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const removeSelect = (id: string) => {
    if (disabled) return;
    onChange(value.filter((item) => item !== id));
  };

  const selectAllAvailable = () => {
    if (disabled) return;
    const availableIds = allOptions.filter((opt) => opt.enabled).map((opt) => opt.id);
    // Keep currently selected unavailable prompts so they are not accidentally wiped, or add all available
    const set = new Set([...value, ...availableIds]);
    onChange(Array.from(set));
  };

  const clearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <label className="block text-sm font-medium text-gray-700">
            适用动作提示词白名单
          </label>
          <span className="text-xs text-gray-400 font-normal">（可选，支持多选）</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500 font-medium">
            已选 <strong className="text-teal-600">{value.length}</strong> / {allOptions.length} 个
          </span>
          {!disabled && allOptions.length > 0 && (
            <>
              <button
                type="button"
                onClick={selectAllAvailable}
                className="text-teal-600 hover:text-teal-800 underline underline-offset-2"
              >
                全选可用
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-gray-500 hover:text-red-600 underline underline-offset-2"
              >
                清空
              </button>
            </>
          )}
        </div>
      </div>

      {/* Advisory notes */}
      {isBatchMode ? (
        <p className="text-xs text-amber-600">
          提示：批量上传导入的模板将统一关联此动作白名单；留空则表示模板不追加动作描述，按「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」拼接生成。
        </p>
      ) : value.length === 0 ? (
        <p className="text-xs text-gray-400">
          当前未选择白名单（空列表）：生成时不追加任何动作描述，万相将直接按「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」正常生成。
        </p>
      ) : null}

      {hasUnavailableSelected && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-1.5 text-xs text-amber-800">
          <i className="ri-error-warning-line text-amber-600 shrink-0 mt-0.5" />
          <span>
            已选列表中包含已停用或已不可用的动作提示词，系统已完整保留历史关联；若不需要该动作请点击标签上的 × 移除。
          </span>
        </div>
      )}

      {/* Selected tags chips display */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 rounded-lg border border-gray-200 max-h-28 overflow-y-auto">
          {selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                opt.enabled
                  ? "bg-teal-50 border-teal-200 text-teal-800"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
              title={opt.text || opt.name}
            >
              <span>{opt.name}</span>
              {!opt.enabled && (
                <span className="text-[10px] px-1 bg-red-100 text-red-800 rounded font-normal">
                  已停用
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeSelect(opt.id)}
                  className="w-3.5 h-3.5 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Search and Picker Container */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* Search input bar */}
        <div className="p-2 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
          <i className="ri-search-line text-gray-400 text-sm ml-1" />
          <input
            type="text"
            disabled={disabled}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索动作提示词名称、描述或标签..."
            className="flex-1 bg-transparent text-xs text-gray-700 focus:outline-none placeholder-gray-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* Action Prompt Checkbox List */}
        <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 p-1">
          {filteredOptions.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-400">
              {allOptions.length === 0
                ? "动作提示词库暂无可用动作，生成将默认按「模板提示词 + 人物年龄性别提示词 + 固定提示词（人体约束）」正常执行"
                : "未搜索到匹配的动作提示词"}
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isChecked = value.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className={`flex items-start gap-2.5 p-2 rounded cursor-pointer transition-colors text-xs hover:bg-gray-50 ${
                    isChecked ? "bg-teal-50/40" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={isChecked}
                    onChange={() => toggleSelect(opt.id)}
                    className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 accent-teal-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 truncate">{opt.name}</span>
                      {!opt.enabled && (
                        <span className="px-1.5 py-0.2 rounded text-[10px] bg-red-100 text-red-700 font-normal">
                          已停用
                        </span>
                      )}
                      {opt.tagsSummary && (
                        <span className="text-[11px] text-gray-400 truncate">
                          [{opt.tagsSummary}]
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5" title={opt.text}>
                      {opt.text}
                    </p>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
